import { useSnackbar } from 'notistack';
import type { FunctionComponent, ReactNode } from 'react';
import { useStore } from 'zustand';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Button } from '~/ui/core/button/Button';
import { SCROLL_FADE_HEADER_PROPS } from '~/ui/core/scroll-fade/ScrollFade';

import { useOptionalUrlTiles } from '../../../url-tiles';
import { useTrackProfileTaskInteraction } from '../analytics/track-profile-task-interaction';
import { useGenerateProfileTask } from '../api/use-generate-profile-task';
import { useProfileTaskDraftCoordinator } from '../draft/ProfileTaskDraftCoordinatorProvider';
import { getProfileTaskParentDraftKey } from '../draft/profile-task-draft-coordinator';
import type { ProfileTaskParentContext } from '../model/profile-task';
import { profileTaskTranslationKeys } from '../profile-task-translations';
import { normalizeProfileTaskTitle } from '../validation/profile-task-validation';
import { ProfileTaskBreadcrumbs } from './ProfileTaskBreadcrumbs';
import { TaskTileHeader } from './profile-task-tile/TaskTileHeader';
import { TaskTitleEditable } from './TaskTitleEditable';
import { createPersistedProfileTaskTilePayload } from './task-tile-url';

/**
 * Title-first creation body of the draft task tile: the title is the entire user input — the
 * backend generates description, workflow, and skills asynchronously after the POST.
 */
export const CreateTaskTileContent: FunctionComponent<{
    readonly context: ProfileTaskParentContext;
    readonly language: LanguageCodeForJSA;
    readonly onRequestClose: () => void;
    readonly taskId: string;
}> = ({ context, language, onRequestClose, taskId }) => {
    const coordinator = useProfileTaskDraftCoordinator();
    const urlTiles = useOptionalUrlTiles();
    const { enqueueSnackbar } = useSnackbar();
    const trackInteraction = useTrackProfileTaskInteraction(context);
    const parentKey = getProfileTaskParentDraftKey(context);
    const editor = useStore(coordinator, (state) => state.parents.get(parentKey)?.editors.get(taskId));

    const generateTask = useGenerateProfileTask({
        context,
        language,
        onError: (error) => {
            enqueueSnackbar(error.message, { variant: 'error' });
        },
        onSuccess: (created) => {
            const taskVersionId = String(created.taskVersion);
            coordinator
                .getState()
                .appendPersistedTask(context, { taskId: created.taskId, taskVersionId, title: created.title ?? '' });
            coordinator.getState().discardEditor(context, taskId);
            trackInteraction({ action: 'task-added', taskId: created.taskId });
            urlTiles?.openTile(
                createPersistedProfileTaskTilePayload({
                    architectureId: context.architectureId,
                    language,
                    parentId: context.parentId,
                    parentType: context.parentType,
                    parentVersion: context.parentVersion,
                    roleId: context.roleId,
                    taskId: created.taskId,
                    taskVersionId,
                }),
            );
        },
    });

    if (editor === undefined) {
        return null;
    }

    const normalizedTitle = normalizeProfileTaskTitle(editor.form.title).trim();
    const submit = (): void => {
        if (normalizedTitle.length === 0 || generateTask.isPending) {
            return;
        }
        generateTask.mutate(normalizedTitle);
    };

    return (
        <TaskCreateBody
            breadcrumbs={<ProfileTaskBreadcrumbs context={context} />}
            isPending={generateTask.isPending}
            title={editor.form.title}
            onClose={onRequestClose}
            onSubmit={submit}
            onTitleChange={(title) => coordinator.getState().updateEditor(context, taskId, { ...editor.form, title })}
        />
    );
};

/**
 * The title-first create body: the title is all the user enters, and "Add task" hands it to `onSubmit`; the
 * backend generates the rest. Shared by every surface that adds a task this way.
 */
export const TaskCreateBody: FunctionComponent<{
    readonly breadcrumbs?: ReactNode;
    readonly isPending: boolean;
    readonly onClose: () => void;
    readonly onSubmit: () => void;
    readonly onTitleChange: (title: string) => void;
    readonly title: string;
}> = ({ breadcrumbs, isPending, onClose, onSubmit, onTitleChange, title }) => {
    const translate = useTranslate();
    const isEmpty = normalizeProfileTaskTitle(title).trim().length === 0;

    return (
        <div className="flex w-full flex-col gap-3xl" data-testid="profile-task-create">
            <div className="sticky top-0 z-[2] bg-surface-raised" {...SCROLL_FADE_HEADER_PROPS}>
                <TaskTileHeader
                    actions={
                        <Button
                            data-testid="profile-task-create-submit"
                            disabled={isPending || isEmpty}
                            size="sm"
                            variant="primary"
                            onClick={onSubmit}
                        >
                            {translate(profileTaskTranslationKeys.addTask)}
                        </Button>
                    }
                    isSticky={false}
                    onClose={onClose}
                />
            </div>

            {breadcrumbs}

            <TaskTitleEditable
                autoFocus={true}
                placeholder={translate(profileTaskTranslationKeys.taskTitle)}
                value={title}
                onChange={onTitleChange}
            />

            <p className="m-0 text-sm leading-normal text-text-secondary">
                {translate(profileTaskTranslationKeys.draftHint)}
            </p>
        </div>
    );
};
